import { MosaicConfig } from '@turnercode/types';

/**
 * Editorial-page gating for Mosaic (Permutive-shaped) events (DAP-114765).
 *
 * Per Mosaic (2026-07-29): capture across ALL pages under the brand domain,
 * EXCLUDING the login page. Video/streaming pages ARE in scope. Gating is a
 * denylist (not an allowlist): fire everywhere except login + any configured
 * `excludedPaths`. An optional `includedPaths` allowlist is supported for
 * brands that want to further narrow the surface. Add/remove pages is
 * config-driven (no code change).
 */

/**
 * Built-in login-page path patterns, always excluded regardless of brand
 * config. Matched case-insensitively against `location.pathname`.
 *
 * Anchored to path-segment boundaries so they match `/login` and
 * `/account/login` but NOT editorial paths that merely contain the word
 * (e.g. `/tech/logins-explained`, `/help/how-to-sign-in-guide`). An unanchored
 * substring match here silently suppresses every Mosaic event on such a page.
 */
export const DEFAULT_LOGIN_PATTERNS: string[] = ['(^|/)log-?in(/|$)', '(^|/)sign-?in(/|$)'];

type MosaicEventKey = keyof NonNullable<MosaicConfig['events']>;

function currentPathname(url?: string): string {
  try {
    if (url) {
      return new URL(url, window.location.href).pathname;
    }
  } catch {
    // fall through to the live location
  }
  return (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
}

function matchesAny(pathname: string, patterns: string[]): boolean {
  return patterns.some((pattern) => {
    try {
      return new RegExp(pattern, 'i').test(pathname);
    } catch {
      // Treat an invalid regex as a plain case-insensitive substring match.
      return pathname.toLowerCase().includes(pattern.toLowerCase());
    }
  });
}

/** True when the given path (or the current page) is a login page. */
export function isLoginPage(url?: string, patterns: string[] = DEFAULT_LOGIN_PATTERNS): boolean {
  return matchesAny(currentPathname(url), patterns);
}

/**
 * Whether Mosaic events may fire on the given page (defaults to the current
 * page). Login pages and any configured `excludedPaths` are always suppressed;
 * when an `includedPaths` allowlist is provided, the path must also match it.
 */
export function isMosaicPageEnabled(config?: MosaicConfig, url?: string): boolean {
  const pathname = currentPathname(url);

  const excluded = [...DEFAULT_LOGIN_PATTERNS, ...(config?.excludedPaths || [])];
  if (matchesAny(pathname, excluded)) {
    return false;
  }

  const included = config?.includedPaths;
  if (included && included.length > 0) {
    return matchesAny(pathname, included);
  }

  return true;
}

/**
 * Whether a specific Mosaic event type is enabled by brand config. Events
 * default to enabled; only an explicit `false` disables one.
 */
export function isMosaicEventEnabled(config: MosaicConfig | undefined, event: MosaicEventKey): boolean {
  const events = config?.events;
  if (!events) {
    return true;
  }
  return events[event] !== false;
}
